import React, { memo, useCallback, useEffect, useMemo, useState } from 'react'
import {
    alpha, Box,
    Dialog,
    DialogContent,
    DialogTitle,
    IconButton,
    TableCell,
    TableRow,
    Tooltip,
    Typography
} from '@mui/material'
import { FiberManualRecord, InfoOutlined } from '@mui/icons-material'
import { useDispatch, useSelector } from 'react-redux';

import { toLocaleStringIn } from '../../util/general';
import {
    Broker_OrderType,
    Order,
    OrderStatus,
    orderStatusToJSON,
    OrderType,
    Product,
    productToJSON,
    TransactionType,
} from '../../proto/generated/order';

import ConfirmationDialog from '../ConfirmationDialog';
import { cancelOrder } from '../../services/apis/orders';
import { QuickTradeData, setQuickOrderData, updateOrderHistory } from '../../reducer/orders.reducer';
import commonActions from '../../actions/common.actions'
import { selectPrimaryTradingAccount, selectBrokers, } from '../../selectors/user'
import { getSecurityAsync, getSecurityKey } from '../../util/security';
import OrderStatusDisplay from '../orderStatus/orderStatusDisplay';
import OrderActionsMenu from './OrderActionsMenu'
import OrderInfoDialog from './OrderInfoDialog'
import OrderHistoryDialog from './OrderHistoryDialog'
import TradeHistoryDialog from './TradeHistoryDialog'
import { selectGroupBy, selectPositions } from '../../selectors/positions';
import { GroupedPosition } from '../../reducer/positions.reducer';
import { getGroupedPositions } from '../../util/positions';
import SlTargetDialog from '../TradePanel/slAndTargetInputs/SlTargetDialog';
import { OrdersNavButtonsDetailed } from '../postionsWidget';
import { selectSnapshot, selectSnapshots } from '../../selectors/security';
import { selectOrdersHistory } from '../../selectors/orders';

const orderTypeToString = (type: Broker_OrderType): string => {
    switch (type) {
        case Broker_OrderType.MARKET_ORDER: return "MARKET";
        case Broker_OrderType.LIMIT_ORDER: return "LIMIT";
        case Broker_OrderType.STOPLOSS_ORDER: return "SL";
        case Broker_OrderType.STOPLOSS_MARKET_ORDER: return "SL-M";
        default: return "UNKNOWN";
    }
};

export enum ModifyType {
    TARGET = 'TARGET',
    STOPLOSS = 'STOPLOSS'
}

const OrderRow: React.FC<{ order: Order; hideOrderStatus?: boolean; tabType: OrdersNavButtonsDetailed }> = ({ order, hideOrderStatus = false, tabType }) => {
    const dispatch = useDispatch()

    const primaryTradingAccount = useSelector(selectPrimaryTradingAccount)
    const brokers = useSelector(selectBrokers)
    const securityKey = getSecurityKey(order.security?.exchange ?? 0, order.security?.code ?? "")
    const [security, setSecurity] = useState<Awaited<ReturnType<typeof getSecurityAsync>>>(undefined)
    useEffect(() => { if (securityKey) getSecurityAsync(securityKey).then(setSecurity) }, [securityKey])
    const securitySnapshot = useSelector(selectSnapshot(securityKey))
    const timestamp = order.exchangeTimestamp || order.created || 0;
    const date = new Date(timestamp);
    const positions = useSelector(selectPositions);
    const groupBy = useSelector(selectGroupBy);
    const orderHistory = useSelector(selectOrdersHistory);
    const groupedPositions: GroupedPosition[] = useMemo(() => {
        const grouped: Record<string, GroupedPosition> = getGroupedPositions(positions, groupBy)
        return Object.values(grouped)
    }, [positions, groupBy])
    const position = useMemo(() => {
        return groupedPositions.find(p => p.groupId === order.basketId)
    }, [groupedPositions])
    const snapshots = useSelector(selectSnapshots)
    const time = date.toLocaleTimeString('en-GB', { hour: '2-digit', minute: '2-digit' });
    const [openSlTarget, setOpenSlTarget] = useState(false)
    const [modifyType, setModifyType] = useState<ModifyType | undefined>(undefined)
    const handleOpenSlTarget = () => {
        if(order.orderType == OrderType.STOP_LOSS){
            setModifyType(ModifyType.STOPLOSS)
        }else{
            setModifyType(ModifyType.TARGET)
        }
        setOpenSlTarget(true)
    }

    const handleCancel = useCallback(async () => {
        if (!order || !order.orderId) return;
        const res = await cancelOrder({
            dispatch,
            body: {
                tradingAccountId: primaryTradingAccount?.accountId ?? "",
                orderId: [order.orderId],
            }
        });

        const responseOrder = res.orderResponse[0];
        if (!responseOrder) return;
        const { successful, commitOrder, errorMessage } = responseOrder;

        if (!successful) {
            commonActions.setApiError(dispatch, {
                error: true,
                message: errorMessage || 'Unable to cancel order',
                errorType: 'error'
            })

            return;
        } else if (commitOrder) {
            dispatch(updateOrderHistory({ updates: [{ ...order, status: OrderStatus.CANCELLED }] }))
        }
    }, [dispatch, order, primaryTradingAccount]);

    const handleEdit = useCallback(() => {
        const quantity = order.unfilledQuantity || order.quantity;
        if (primaryTradingAccount?.accountId) {
            let mode: QuickTradeData["mode"] = 'regular';

            switch (order.orderType) {
                case OrderType.TRIGGER:
                    mode = "trigger";
                    break;
                case OrderType.STOP_LOSS:
                case OrderType.TARGET:
                    mode = "slOrTarget"
                    break;
            }
            dispatch(setQuickOrderData({
                mode,
                openDialog: true,
                type: 'modify',
                isSquareOffOrder: false,
                maxQuantity: quantity,
                order: {
                    ...order,
                    quantity
                }
            }));
        }
    }, [dispatch, order, primaryTradingAccount]);

    const [infoOpen, setInfoOpen] = useState(false)
    const [historyOpen, setHistoryOpen] = useState(false)
    const [tradesOpen, setTradesOpen] = useState(false)

    const currentBroker = useMemo(() => {
        return brokers.find(broker => broker.code === primaryTradingAccount?.brokerCode)
    }, [brokers, primaryTradingAccount?.brokerCode])



    const openInfo = useCallback(() => setInfoOpen(true), [])
    const closeAll = useCallback(() => {
        setInfoOpen(false)
        setHistoryOpen(false)
        setTradesOpen(false)
    }, [])
    const viewHistory = useCallback(() => {
        setInfoOpen(false)
        setHistoryOpen(true)
    }, [])
    const viewTrades = useCallback(() => {
        setInfoOpen(false)
        setTradesOpen(true)
    }, [])
    const backToInfo = useCallback(() => {
        setHistoryOpen(false)
        setTradesOpen(false)
        setInfoOpen(true)
    }, [])

    const displaySymbol = useMemo(() => {
        return order.isBasket && [OrderType.STOP_LOSS, OrderType.TARGET].includes(order.orderType) ? position?.groupName ?? "default" : security?.symbol
    }, [order, security])

    const symbolRowPadding = useMemo(() => {
        return hideOrderStatus ? '5px' : '5px 5px 0'
    }, [hideOrderStatus]);
    const symbolRowBorderBottom = useMemo(() => {
        return hideOrderStatus ? (theme: any) => `1.5px solid ${theme.palette.background.default}` : 'none'
    }, [hideOrderStatus]);

    const isSingleLeg = useMemo(() => {
        if (!order.isBasket) return true;
        const count = orderHistory.filter(
            to => to.batchId === order.batchId
        ).length;
        return count === 1;
    }, [order, orderHistory]);

    const strategyMtm = useMemo(() => {
        if (!position || position.children?.length < 1) return 0;
        const { security, realizedProfit, quantity, children = [] } = position;
        if (quantity === 0) return realizedProfit/(security.priceMultiplier??100);
        let profit = realizedProfit
        children.forEach(childPos => {
            const childSecKey = getSecurityKey(childPos.security.exchange, childPos.security.code);
            const childSnapshot = snapshots[childSecKey];
            if (childSnapshot) {
                profit += (childSnapshot.lastTradedPrice - childPos.avgPrice)*childPos.quantity;
            }
        });
        return profit/(security.priceMultiplier??100);
    }, [position, snapshots]);

    const strategyAvgPrice = useMemo(() => {
        if (!position || position.children?.length < 1) return 0;
        const { security, quantity, children = [] } = position;
        if (quantity === 0) return 0;
        let avg = 0
        children.forEach(childPos => {
            avg += childPos.avgPrice * childPos.quantity
        });
        return Math.abs(avg/(security.priceMultiplier??100)/ position.quantity)
    }, [position]);

    const avgPrice = useMemo(() => {
        if(isSingleLeg || (order.orderType !== OrderType.STOP_LOSS && order.orderType !== OrderType.TARGET)){
            return toLocaleStringIn((order.averageTradedPrice ?? 0) / (security?.priceMultiplier ?? 100))
        }
        return strategyAvgPrice
    }, [isSingleLeg, security, order, strategyAvgPrice])
    
    const ltpOrMtm = useMemo(() => {
        if(!order.isBasket || (order.orderType !== OrderType.STOP_LOSS && order.orderType !== OrderType.TARGET)){
            return toLocaleStringIn((securitySnapshot?.lastTradedPrice ?? 0) / (security?.priceMultiplier ?? 100))
        }
        return strategyMtm
    }, [security, securitySnapshot, order, strategyMtm])

    const isSLorTarget = useMemo(() => {
        return [OrderType.STOP_LOSS, OrderType.TARGET].includes(order.orderType);
    }, [order.orderType])

    return (
        <>
            <TableRow
                sx={{
                    opacity: [OrderStatus.CANCELLED, OrderStatus.FAILED].includes(order.status ?? 0) ? 0.5 : 1,
                    position: 'relative',
                    '&:hover .edit-icon-order': { display: 'table-cell !important' },
                    '&:hover .edit-icon-replacement': { display: 'none !important' },
                    '&:hover .order-actions': { visibility: 'visible' }
                }}
            >
                <TableCell sx={{ width: '25%', padding: symbolRowPadding, paddingLeft: 0, verticalAlign: 'top', borderBottom: symbolRowBorderBottom }}>
                    <Box sx={{ display: 'flex', flexDirection: 'column', px: 1 }}>
                        <Typography fontSize={12} sx={{ whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis' }} title={`${displaySymbol}`}>{displaySymbol} &nbsp;
                        </Typography>
                    </Box>
                </TableCell>

                <TableCell sx={{ width: '5%', padding: symbolRowPadding, textAlign: 'center', verticalAlign: 'top', borderBottom: symbolRowBorderBottom }}>
                    {isSingleLeg || (!isSingleLeg && isSLorTarget) ? 
                    <Typography fontSize="13px" fontWeight={isSLorTarget ? "400" : "500"} sx={{ color: isSLorTarget ? null :  theme => theme.palette[order.transactionType === TransactionType.BUY ? 'buy' : 'sell'].main }}>
                        {order.orderType === OrderType.STOP_LOSS ? 'SL' : order.orderType == OrderType.TARGET ? 'TP' :  order.transactionType === TransactionType.BUY ? 'B' : 'S' }
                    </Typography>: '-' 
                    }
                </TableCell>

                <TableCell sx={{ width: '8%', padding: symbolRowPadding, textAlign: 'right', verticalAlign: 'top', borderBottom: symbolRowBorderBottom }}>
                    <Typography fontSize="13px">{order.filledQuantity}
                        <>{'/'}{order.quantity}</>
                    </Typography>
                </TableCell>

                <TableCell sx={{ width: '30%', padding: symbolRowPadding, textAlign: 'right', verticalAlign: 'top', borderBottom: (theme) => `1.5px solid ${theme.palette.background.default}` }} rowSpan={hideOrderStatus ? 1 : 2}>
                    <Typography fontSize="13px">
                        {
                            Broker_OrderType.STOPLOSS_MARKET_ORDER === order.brokerOrderType ?
                                toLocaleStringIn(order.triggerPrice / (security?.priceMultiplier ?? 100)) :
                                    Broker_OrderType.STOPLOSS_ORDER === order.brokerOrderType ?
                                        toLocaleStringIn(order.price / (security?.priceMultiplier ?? 100)) + ' / ' + toLocaleStringIn((order.triggerPrice ?? 0) / (security?.priceMultiplier ?? 100)) :
                                        toLocaleStringIn(order.price / (security?.priceMultiplier ?? 100))
                        }
                    </Typography>
                </TableCell>

                {tabType === OrdersNavButtonsDetailed.OPEN ? <TableCell sx={{ width: '15%', padding: symbolRowPadding, pr: '2px', textAlign: 'right', verticalAlign: 'top', borderBottom: (theme) => `1.5px solid ${theme.palette.background.default}`, position: 'relative' }} rowSpan={hideOrderStatus ? 1 : 2}>
                    <Typography fontSize="13px">{ltpOrMtm}</Typography>
                    <Box className="order-actions"
                        sx={{
                            position: 'absolute',
                            top: 0,
                            right: 0,
                            height: '100%',
                            display: 'flex',
                            visibility: 'hidden',
                            background: (theme) => alpha(theme.palette.background.paper, 0.9),
                            alignItems: 'center',
                            zIndex: 2,
                            borderRadius: '4px'
                        }}
                        onClick={(e) => e.stopPropagation()}
                    >
                        <OrderActionsMenu order={order} onEdit={handleEdit} onCancel={handleCancel} onInfo={openInfo} onOpenSlTarget={handleOpenSlTarget} />
                    </Box>
                </TableCell> : <TableCell sx={{ width: '15%', padding: symbolRowPadding, pr: '2px', textAlign: 'right', verticalAlign: 'top', borderBottom: (theme) => `1.5px solid ${theme.palette.background.default}`, position: 'relative' }} rowSpan={hideOrderStatus ? 1 : 2}>
                    <Typography fontSize="13px">{avgPrice}</Typography>
                    <Box className="order-actions"
                        sx={{
                            position: 'absolute',
                            top: 0,
                            right: 0,
                            height: '100%',
                            display: 'flex',
                            visibility: 'hidden',
                            background: (theme) => alpha(theme.palette.background.paper, 0.9),
                            alignItems: 'center',
                            zIndex: 2,
                            borderRadius: '4px'
                        }}
                        onClick={(e) => e.stopPropagation()}
                    >
                        <OrderActionsMenu order={order} onEdit={handleEdit} onCancel={handleCancel} onInfo={openInfo} onOpenSlTarget={handleOpenSlTarget} />
                    </Box>
                </TableCell>}
            </TableRow>
            {!hideOrderStatus && (<TableRow
                sx={{
                    borderBottom: symbolRowBorderBottom,
                    opacity: [OrderStatus.CANCELLED, OrderStatus.FAILED].includes(order.status ?? 0) ? 0.5 : 1,
                    position: 'relative',
                    '&:hover .edit-icon-order': { display: 'table-cell !important' },
                    '&:hover .edit-icon-replacement': { display: 'none !important' },
                    '&:hover .order-actions': { visibility: 'visible' }
                }}
            >
                <TableCell sx={{ padding: '0 0 5px', verticalAlign: 'top', borderBottom: (theme) => `1.5px solid ${theme.palette.background.default}` }} colSpan={3}>
                    <Box sx={{ display: 'flex', flexDirection: 'column', px: 1 }}>
                        <Box sx={{ display: 'flex', alignItems: 'center', mt: 0.3 }}>
                            <Typography fontSize="12px" color="text.secondary">{time}</Typography>
                            <Typography fontSize="12px" marginLeft={'6px'} color="text.secondary" variant="caption">{productToJSON(order.product)}</Typography>
                            <Typography fontSize="12px" marginLeft={'6px'} color="text.secondary" variant="caption">{orderTypeToString(order.brokerOrderType)}</Typography>
                            <span style={{ fontWeight: 500, fontSize: '12px' }} className={
                                order.status === OrderStatus.EXECUTED ? 'green-text' :
                                    order.status === OrderStatus.OPEN ? 'red-text' : ''
                            }>
                                <OrderStatusDisplay status={order.status} style={{ fontWeight: 500, fontSize: '12px', marginLeft: '6px', lineHeight: 1.66 }} />
                            </span>
                            {(order.status === OrderStatus.FAILED && order.message?.length && order.message?.length > 0) ? (
                                <Tooltip title={<Typography fontSize="12px">{order.message}</Typography>} arrow>
                                    <IconButton size="small" disableRipple sx={{ height: 16, width: 16, ml: 0.5, background: (theme) => alpha(theme.palette.background.paper, 0.7) }}>
                                        <InfoOutlined sx={{ height: 16, width: 16 }} />
                                    </IconButton>
                                </Tooltip>
                            ) : null}
                        </Box>
                    </Box>
                </TableCell>
            </TableRow>)}
            <OrderInfoDialog
                open={infoOpen}
                onClose={closeAll}
                onViewTrades={viewTrades}
                onViewHistory={viewHistory}
                order={order}
                broker={currentBroker}
            />
            {openSlTarget ? <SlTargetDialog open={openSlTarget} position={position} onClose={() => {setOpenSlTarget(false); setModifyType(undefined)}}  modifyType={modifyType} /> : null}
            <OrderHistoryDialog open={historyOpen} onClose={closeAll} onBack={backToInfo} order={order} accountId={primaryTradingAccount?.accountId ?? ''} />
            <TradeHistoryDialog open={tradesOpen} onClose={closeAll} onBack={backToInfo} order={order} accountId={primaryTradingAccount?.accountId ?? ''} />
        </>
    )
};

export default memo(OrderRow);